import { zodResolver } from '@hookform/resolvers/zod';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { InputNumber } from '@plato/ui-legacy/components/ui/input-number';
import { useFormExtended } from '@plato/ui-legacy/lib/hooks/use-form-extended';
import { useOnFormValuesChange } from '@plato/ui-legacy/lib/hooks/use-on-form-values-change';
import { useTrackedFieldChanges } from '@plato/ui-legacy/lib/hooks/use-tracked-field-changes';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@plato/ui/containers/dropdown-menu';
import { MenuItemLabel, MenuItemLeading } from '@plato/ui/primitives/menu';
import { cn } from '@plato/ui/util/classes';
import { ChevronRightIcon, EditIcon, EllipsisVerticalIcon, Trash2, Trash2Icon } from 'lucide-react';
import type { ReactElement } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import type { ProductVariantDTO } from '../../../server/dto/product-variant-dto.ts';
import {
    salesDiscountSchema,
    type SalesDraftNormalLineItem,
    type SalesDraftOrder,
} from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { draftLineItemFields } from '../../analytics/types/analytics-events.ts';
import { ProductLinkCell } from '../../list-views/components/product-link-cell.tsx';
import { FieldError } from '../form/field-error.tsx';
import { IncrementalQuantityFieldWithDialog } from '../form/incremental-quantity-field.tsx';
import { UnitPriceInput } from '../form/unit-price-input.tsx';
import { useDraftContext } from '../hooks/use-draft-context.ts';
import { useToasts } from '../hooks/use-toasts.ts';
import { LineItemEditDialog } from '../views/line-item-edit-dialog.tsx';
import { LineItemRowBase, LineItemRowCell } from './line-items.tsx';
import { RelatedItems } from './related-items.tsx';

export type ProductLineItemProps = {
    draft: SalesDraftOrder;
    lineItem: SalesDraftNormalLineItem;
    /** Whether this row's details are expanded. Owned by the parent so only one row is open at a time. */
    expanded: boolean;
    /** Request this row be opened (`true`) or closed (`false`). */
    onOpenChange: (open: boolean) => void;
    /** Add a related item (from this row's related-items strip) to the draft. */
    onSelectRelatedProduct: (product: ProductVariantDTO) => void;
};

const updateProductRowFormSchema = z.object({
    price: z.number(),
    quantity: z.number(),
    rebate: z.array(salesDiscountSchema),
});

export function ProductLineItemRow({
    draft,
    lineItem,
    expanded,
    onOpenChange,
    onSelectRelatedProduct,
}: ProductLineItemProps): ReactElement {
    const { t } = useTranslation('sales-draft');
    const customerViewToggled = useIsCustomerMode();
    const { setDraft } = useDraftContext({ refetchOnMount: false });
    const { showUpdateErrorToast } = useToasts();
    const sendEvent = useAnalytics();

    const {
        canReadGrossMargin,
        canReadTransactionGrossMargin,
        canReadLineItemRebate,
        canUpdateLineItemPrice,
        canUpdateLineItemRebate,
        canReadDraftLineItemBackorder,
        enableDraftRelatedItems,
        defaultCurrency,
    } = useAppConfig();

    const storageId = lineItem.storage_id || draft.storage_id;

    const { control, subscribe } = useFormExtended({
        mode: 'all',
        values: {
            price: lineItem.price,
            quantity: lineItem.quantity,
            rebate: lineItem.rebate ?? [],
        },
        resolver: zodResolver(updateProductRowFormSchema),
    });

    const { fields: rebateFields } = useFieldArray({
        control,
        name: 'rebate',
    });

    const { data: salesConfig, isLoading } =
        api.platoUser.query.salesDraft.getSalesDraft.getSalesConfig.useQuery({
            product_variant_id: lineItem.product_variant_id,
        });

    const { data: currentStock, error: stockError } =
        api.platoUser.query.salesDraft.getInventory.getProductVariantConfig.useQuery(
            { productVariantId: lineItem.product_variant_id },
            {
                enabled: canReadDraftLineItemBackorder,
                select: (data) => data.stock.find((x) => x.storage_id === storageId)?.effective ?? 0,
            },
        );

    const salesUnit = salesConfig?.quantity_unit_sales ?? salesConfig?.package_unit;

    const { mutate: onDelete, isPending: isDeletePending } =
        api.platoUser.command.salesDraft.lineItems.deleteNormalLineItem.useMutation({
            onError: showUpdateErrorToast,
            onSuccess: setDraft,
        });

    const { mutate } = api.platoUser.command.salesDraft.lineItems.updateNormalLineItem.useMutation({
        onError: showUpdateErrorToast,
        onSuccess: setDraft,
    });

    useOnFormValuesChange({
        subscribe,
        onChange: (data) => {
            if (!salesUnit) {
                return;
            }
            mutate({
                draft_line_item_id: lineItem.line_item_id,
                draft_order_id: draft.draft_order_id,
                external: lineItem.external,
                note: lineItem.note ?? '',
                price: data.price,
                product_variant_id: lineItem.product_variant_id,
                quantity: data.quantity,
                quantity_unit: salesUnit,
                rebate: data.rebate,
                shipping_type_id: lineItem.shipping_type_id,
                storage_id: lineItem.storage_id,
            });
        },
    });

    useTrackedFieldChanges({
        subscribe,
        fields: draftLineItemFields,
        onFieldChange: (field) => {
            sendEvent('draft_line_item_field_changed', {
                draft_id: draft.draft_order_id,
                line_item_id: lineItem.line_item_id,
                product_variant_id: lineItem.product_variant_id,
                field,
            });
        },
    });

    function handleEdit(): void {
        sendEvent('draft_line_item_edit_more_clicked', {
            draft_id: draft.draft_order_id,
            line_item_id: lineItem.line_item_id,
            product_variant_id: lineItem.product_variant_id,
        });

        void DialogStateManager.show(LineItemEditDialog, {
            draft,
            lineItem,
        });
    }

    function handleDelete(): void {
        onDelete({
            draft_order_id: draft.draft_order_id,
            draft_line_item_id: lineItem.line_item_id,
        });
    }

    return (
        <LineItemRowBase data-testid="draft-line-item-row">
            <div className="flex items-center gap-2 p-2 sm:contents">
                {/* Chevron + product name form the first desktop column; the chevron
                    toggles the related-items strip without opening the product drawer. */}
                <div className="flex min-w-0 grow items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-expanded={expanded}
                        aria-label={lineItem.product_variant_name}
                        className={cn('shrink-0 text-neutral-500', !enableDraftRelatedItems && 'sm:hidden')}
                        onClick={() => {
                            onOpenChange(!expanded);
                        }}
                    >
                        <ChevronRightIcon
                            className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')}
                        />
                    </Button>
                    <div
                        className="min-w-0 grow"
                        onClick={() => {
                            onOpenChange(!expanded);
                        }}
                    >
                        <ProductLinkCell
                            erpProductId={lineItem.product_variant_id}
                            productName={lineItem.product_variant_name}
                            className="p-0"
                        />
                    </div>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 sm:hidden"
                    aria-label={t('sales_draft__delete__line_item', {})}
                    disabled={isDeletePending}
                    onClick={handleDelete}
                    icon={Trash2Icon}
                />
            </div>

            {/* Collapsible field grid on mobile; on desktop `contents` turns each field into a column. */}
            <div
                className={cn(
                    'grid grid-cols-2 gap-3 border-t border-neutral-100 p-3 sm:contents',
                    !expanded && 'hidden',
                )}
            >
                <LineItemRowCell label={t('sales_draft__line_items__table_header__quantity')}>
                    {salesUnit || isLoading ? (
                        <Controller
                            control={control}
                            name="quantity"
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <IncrementalQuantityFieldWithDialog
                                        currency={defaultCurrency}
                                        quantityUnit={salesUnit ?? ''}
                                        salesUnitsPerPackageUnit={salesConfig?.sales_units_per_package_unit}
                                        partialPackingType={salesConfig?.partial_packing_type}
                                        onBlur={field.onBlur}
                                        onValueChange={field.onChange}
                                        value={field.value}
                                        isLoading={isLoading}
                                    />
                                    <FieldError error={error} />
                                    {stockError ? (
                                        <div className="text-sm text-red-600">
                                            {t('sales_draft__error_stock')}
                                        </div>
                                    ) : canReadDraftLineItemBackorder &&
                                      currentStock !== undefined &&
                                      currentStock < field.value ? (
                                        <div className="text-sm text-red-600">
                                            {t('backorder', { ns: 'common' })}: {field.value - currentStock}
                                        </div>
                                    ) : undefined}
                                </>
                            )}
                        />
                    ) : (
                        <span className="text-red-600">{t('sales_draft__error_no_unit')}</span>
                    )}
                </LineItemRowCell>
                <LineItemRowCell label={t('sales_draft__line_items__table_header__price')}>
                    <Controller
                        name="price"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <>
                                <UnitPriceInput
                                    name={field.name}
                                    disabled={!canUpdateLineItemPrice}
                                    loading={isLoading}
                                    onBlur={field.onBlur}
                                    onValueChange={field.onChange}
                                    scaleFactor={salesConfig?.price_scale_factor}
                                    unit={salesUnit}
                                    value={field.value}
                                />
                                <FieldError error={error} />
                            </>
                        )}
                    />
                </LineItemRowCell>
                {canReadLineItemRebate && (
                    <LineItemRowCell label={t('sales_draft__line_items__table_header__rebate')}>
                        <div className="flex flex-row flex-wrap gap-2">
                            {rebateFields.map((field, index) => (
                                <Controller
                                    key={field.id}
                                    control={control}
                                    name={`rebate.${index}.percent`}
                                    render={({ field, fieldState: { error } }) => (
                                        <div className="flex-1 min-w-14">
                                            <InputNumber
                                                currency={defaultCurrency}
                                                name={field.name}
                                                value={field.value}
                                                suffix={` %`}
                                                loading={isLoading}
                                                onValueChange={({ floatValue }) => {
                                                    field.onChange(floatValue ?? 0);
                                                }}
                                                onBlur={field.onBlur}
                                                isAllowed={({ floatValue = 0 }) =>
                                                    floatValue >= 0 && floatValue <= 100
                                                }
                                                disabled={!canUpdateLineItemRebate}
                                            />
                                            <FieldError error={error} />
                                        </div>
                                    )}
                                />
                            ))}
                        </div>
                    </LineItemRowCell>
                )}
                {canReadGrossMargin && canReadTransactionGrossMargin && !customerViewToggled && (
                    <LineItemRowCell label={t('sales_draft__line_items__table_header__gross_margin_percent')}>
                        <InputNumber
                            currency={defaultCurrency}
                            name="gross_margin"
                            disabled
                            value={lineItem.total ? (100 * lineItem.gross_margin) / lineItem.total : 0}
                            suffix={`%`}
                            decimalScale={2}
                        />
                    </LineItemRowCell>
                )}
                <div className="col-span-2 sm:hidden">
                    <Button variant="subtle" className="w-full" leftIcon={EditIcon} onClick={handleEdit}>
                        {t('sales_draft__edit__line_item', {})}
                    </Button>
                </div>
            </div>

            {/* Desktop-only actions menu (mobile uses the inline trash + edit button above). */}
            <div className="hidden sm:flex sm:justify-end">
                <DropdownMenu>
                    <DropdownMenuTrigger
                        render={
                            <Button variant="ghost">
                                <EllipsisVerticalIcon className="h-5 w-5" />
                            </Button>
                        }
                    />
                    <DropdownMenuContent align="end" side="bottom">
                        <DropdownMenuItem onClick={handleEdit}>
                            <MenuItemLeading>
                                <EditIcon />
                            </MenuItemLeading>
                            <MenuItemLabel>{t('sales_draft__edit__line_item', {})}</MenuItemLabel>
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={isDeletePending} onClick={handleDelete}>
                            <MenuItemLeading>
                                <Trash2 />
                            </MenuItemLeading>
                            <MenuItemLabel>{t('sales_draft__delete__line_item', {})}</MenuItemLabel>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Related items strip: full-width below the row's cells when expanded. */}
            {enableDraftRelatedItems && expanded && (
                <RelatedItems
                    draft={draft}
                    productVariantId={lineItem.product_variant_id}
                    onSelectProduct={onSelectRelatedProduct}
                />
            )}
        </LineItemRowBase>
    );
}
